import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import c from 'classnames';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useDispatch, useSelector } from 'react-redux';
import { isToolUIPart, getToolName } from 'ai';
import { getToolPartState, getToolApproval, getToolInput, getToolCallId } from '@cloudflare/ai-chat/react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link, useLocation } from 'react-router-dom';
// import { is } from 'date-fns/locale';

import { APP_URL } from '../../../../config';
import { setAiAssistantMode } from '../../../../sagas/aiAssistant/actions';

import { ReactComponent as CloseDarkIcon } from '../../../../assetsNew/icons/ai/close-dark.svg';
import { ReactComponent as CopyIcon } from '../../../../assetsNew/icons/ai/content_copy.svg';
import { ReactComponent as FileDownloadIcon } from '../../../../assetsNew/icons/ai/file_download.svg';
import { ReactComponent as RefreshIcon } from '../../../../assetsNew/icons/ai/refresh.svg';
import { ReactComponent as SparklesOutlinedIcon } from '../../../../assetsNew/icons/ai/sparkles-outlined.svg';
import { ReactComponent as SparklesFilledIcon } from '../../../../assetsNew/icons/ai/sparkles-filled.svg';

import AIAssistantQuickReplies from './components/AIAssistantQuickReplies';

import './_aiAssistantMessages.scss';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const TOOL_LABELS = {
  analytics_by_creator: 'Analyzing creator performance',
  analytics_by_link: 'Analyzing link performance',
  analytics_by_campaign: 'Analyzing campaign performance',
  get_campaign_modifiers: 'Loading campaign modifiers',
  create_campaign: 'Creating campaign',
  get_campaign: 'Loading campaign details',
  list_campaigns: 'Loading campaigns',
  get_campaign_stats: 'Loading campaign stats',
  get_campaign_participants: 'Loading campaign participants',
  create_shopping_reimbursement: 'Creating shopping reimbursement',
  list_shopping_reimbursements: 'Loading shopping reimbursements',
  update_campaign: 'Updating campaign',
  archive_campaign: 'Archiving campaign',
  delete_campaign: 'Deleting campaign',
  add_creators_to_campaign: 'Adding creators to campaign',
  remove_creators_from_campaign: 'Removing creators from campaign',
  search_links: 'Searching links',
  create_link: 'Creating affiliate link',
  update_link: 'Updating link',
  search_creators: 'Searching creators',
  get_creator_details: 'Loading creator profile',
  get_creator_brand_performance: 'Loading brand performance',
  get_creator_global_metrics: 'Loading global metrics',
  get_similar_creators: 'Finding similar creators',
  tag_creator: 'Updating creator tags',
  add_tags_to_creators: 'Adding tags to creators',
  list_tags: 'Loading tags',
  list_groups: 'Loading groups',
  create_group: 'Creating group',
  get_group_details: 'Loading group details',
  add_users_to_group: 'Adding creators to group',
  remove_users_from_group: 'Removing creators from group',
  delete_group: 'Deleting group',
  send_message: 'Sending message',
  send_message_to_group: 'Sending message to group',
  list_message_tasks: 'Loading message history',
  get_message_task: 'Loading message details',
  retry_failed_messages: 'Retrying failed messages',
  get_store_domains: 'Loading store domains',
  get_verified_domains: 'Loading verified domains',
  get_commission_rates: 'Loading commission rates',
  stage_connection_invite_proposal: 'Pinning invite candidates',
  execute_connection_invite_proposal: 'Sending connection invites',
  review_creator_relationship: 'Reviewing creator relationship',
  suggest_choices: 'Preparing quick replies'
};

// The creator panel's "Help me with next steps" button sends the @relationship preset
// with a channel_url; show the user a readable label instead of the raw command.
const RELATIONSHIP_PRESET_RE = /^@relationship\s+(.+?)(?:\s+channel_url=\S+)?\s*$/;

const formatUserText = text => {
  const match = RELATIONSHIP_PRESET_RE.exec(text || '');
  return match ? `Help me with next steps for ${match[1]}` : text;
};

export const getToolLabel = toolName => TOOL_LABELS[toolName] || toolName.replace(/_/g, ' ');

const isToolPartByType = part => part.type.startsWith('tool-');

// The agent ends a reply with at most one suggest_choices call; its choices become quick-reply buttons.
const getQuickReplyChoices = message => {
  const part = (message?.parts || []).find(p => p.type === 'tool-suggest_choices' && p.state === 'output-available');
  const choices = part?.input?.choices || part?.output?.choices;
  if (!Array.isArray(choices)) return [];
  return [...new Set(choices.filter(choice => typeof choice === 'string' && choice.trim()))];
};

const findNameInToolOutput = (output, uuid) => {
  if (!output || typeof output !== 'object') return null;
  const stack = [output];
  while (stack.length) {
    const node = stack.pop();
    if (Array.isArray(node)) {
      for (const v of node) stack.push(v);
      continue;
    }
    if (!node || typeof node !== 'object') continue;
    const o = node;
    const user = o.user;
    if (user) {
      if (user.uuid === uuid || user.private_uuid === uuid) {
        return user.name || o.user_name || null;
      }
      stack.push(user);
    }
    if (o.uuid === uuid || o.private_uuid === uuid) {
      return o.name || null;
    }
    for (const v of Object.values(o)) {
      if (v && typeof v === 'object') stack.push(v);
    }
  }
  return null;
};

const resolveCreatorNameFromMessages = (messages, uuid) => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    for (const part of msg.parts || []) {
      if (!isToolPartByType(part)) continue;
      if (part.state !== 'output-available') continue;
      const name = findNameInToolOutput(part.output, uuid);
      if (name) return name;
    }
  }
  return null;
};

const resolvePinnedCreatorsFromMessages = (messages, proposalId) => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    for (const part of msg.parts || []) {
      if (!isToolPartByType(part)) continue;
      if (part.type !== 'tool-stage_connection_invite_proposal') continue;
      if (part.state !== 'output-available') continue;

      const output = part.output;
      if (!output?.creators?.length) continue;
      if (proposalId && output.proposal_id !== proposalId) continue;

      return output.creators.filter(creator => Boolean(creator?.creator_name && creator?.creator_uuid));
    }
  }
  return null;
};

const resolveGroupInfoFromMessages = (messages, { groupId, groupName }) => {
  const normalizedName = groupName?.trim()?.toLowerCase();

  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    for (const part of msg.parts || []) {
      if (!isToolPartByType(part)) continue;
      if (part.type !== 'tool-list_groups') continue;
      if (part.state !== 'output-available') continue;

      const results = part.output?.results;
      if (!Array.isArray(results)) continue;

      const match = results.find(group => {
        const byName = normalizedName && String(group?.name || '').trim().toLowerCase() === normalizedName;
        const byId = groupId != null && (group?.id === groupId || group?.group_id === groupId);
        return Boolean(byName || byId);
      });

      if (match) {
        return {
          name: match.name || undefined,
          total_users: match.total_users ?? match.users_count ?? undefined
        };
      }
    }
  }

  return null;
};

const getMessageMarkdown = message => {
  if (!message?.parts) return message?.content || '';
  return message.parts
    .filter(part => part.type === 'text')
    .map(part => part.text)
    .join('\n\n');
};

const TABLE_SEPARATOR_REGEX = /^\s*\|?(?:\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?\s*$/;

const isMarkdownTableSeparator = line => TABLE_SEPARATOR_REGEX.test(line);

const splitMarkdownTableRow = line => {
  const normalized = line.trim();
  const strippedStart = normalized.startsWith('|') ? normalized.slice(1) : normalized;
  const stripped = strippedStart.endsWith('|') ? strippedStart.slice(0, -1) : strippedStart;
  return stripped.split('|').map(cell => cell.trim());
};

const escapeCsvCell = value => {
  const text = String(value ?? '');
  return `"${text.replace(/"/g, '""')}"`;
};

const tableToCsv = table => table
  .map(row => row.map(escapeCsvCell).join(','))
  .join('\n');

const extractFirstMarkdownTable = markdown => {
  if (!markdown) return null;

  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  for (let i = 0; i < lines.length - 1; i++) {
    const headerLine = lines[i].trim();
    const separatorLine = lines[i + 1].trim();

    if (!headerLine.includes('|') || !isMarkdownTableSeparator(separatorLine)) continue;

    const rows = [splitMarkdownTableRow(headerLine)];
    let rowIndex = i + 2;

    while (rowIndex < lines.length) {
      const currentLine = lines[rowIndex].trim();
      if (!currentLine || !currentLine.includes('|')) break;
      if (currentLine.startsWith('```')) break;
      rows.push(splitMarkdownTableRow(currentLine));
      rowIndex += 1;
    }

    return rows;
  }

  return null;
};

const hasMarkdownTableSyntax = markdown => {
  if (!markdown) return false;

  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  for (let i = 0; i < lines.length - 1; i++) {
    const currentLine = lines[i].trim();
    const nextLine = lines[i + 1].trim();

    if (currentLine.includes('|') && isMarkdownTableSeparator(nextLine)) {
      return true;
    }
  }

  return false;
};

const getMarkdownSectionsWithTablePlaceholders = markdown => {
  if (!markdown) return [{ type: 'markdown', content: '' }];

  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const sections = [];
  const buffer = [];

  let lineIndex = 0;
  while (lineIndex < lines.length) {
    const currentLine = lines[lineIndex].trim();
    const nextLine = lines[lineIndex + 1]?.trim();

    if (
      lineIndex < lines.length - 1
      && currentLine.includes('|')
      && isMarkdownTableSeparator(nextLine)
    ) {
      let endIndex = lineIndex + 2;
      while (endIndex < lines.length) {
        const line = lines[endIndex].trim();
        if (!line || !line.includes('|') || line.startsWith('```')) break;
        endIndex += 1;
      }

      const isComplete = endIndex < lines.length;

      if (isComplete) {
        buffer.push(...lines.slice(lineIndex, endIndex));
      } else {
        if (buffer.join('\n').trim()) {
          sections.push({ type: 'markdown', content: buffer.join('\n') });
          buffer.length = 0;
        }
        sections.push({ type: 'table-placeholder' });
      }

      lineIndex = endIndex;
      continue;
    }

    buffer.push(lines[lineIndex]);
    lineIndex += 1;
  }

  if (buffer.join('\n').trim() || sections.length === 0) {
    sections.push({ type: 'markdown', content: buffer.join('\n') });
  }

  return sections;
};

const downloadCsv = (csv, filename) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.rel = 'noopener noreferrer';
  link.click();

  URL.revokeObjectURL(url);
};

const formatApprovalSummary = (toolName, input) => {
  switch (toolName) {
    case 'send_message': {
      const msg = input.message || '';
      const userIds = input.user_ids;
      const count = userIds?.length || 0;
      const confirm = input.confirm;
      if (confirm === false) return `**Preview message** to ${count} creator${count !== 1 ? 's' : ''}:\n\n> ${msg}`;
      return `**Send message** to ${count} creator${count !== 1 ? 's' : ''}:\n\n> ${msg}`;
    }
    case 'send_message_to_group': {
      const msg = input.message || '';
      const groupName = input._resolved_group_name;
      const total = input._resolved_group_total;
      const who = groupName && total != null
        ? `all ${total} creators in "${groupName}"`
        : groupName
          ? `everyone in "${groupName}"`
          : 'the whole group';
      return `**Send message** to ${who}:\n\n> ${msg}`;
    }
    case 'create_campaign': {
      const name = input.name || 'Untitled';
      const start = (input.date_start || '').split('T')[0];
      const end = (input.date_end || '').split('T')[0];
      const users = input.users;
      const count = users?.length || 0;
      const status = input.status || 'draft';
      const bonuses = input.bonuses;
      let bonusInfo = '';
      if (bonuses?.length) {
        const b = bonuses[0];
        const reward = b.reward || '?';
        const links = b.qualified_links_required || '?';
        const clicks = b.minimum_clicks || '?';
        bonusInfo = `\n- Bonus: $${reward} per creator\n- Links required: ${links}\n- Min clicks/link: ${clicks}`;
      }
      return `**Create campaign** "${name}"\n- ${count} creator${count !== 1 ? 's' : ''}\n- ${start} to ${end}\n- Status: ${status}${bonusInfo}`;
    }
    case 'update_campaign': {
      const id = input.campaign_id;
      const fields = Object.keys(input).filter(k => k !== 'campaign_id').join(', ');
      return `**Update campaign** #${id}\n- Fields: ${fields}`;
    }
    case 'archive_campaign':
      return `**Archive campaign** #${input.campaign_id}`;
    case 'delete_campaign':
      return `**Delete campaign** #${input.campaign_id}`;
    case 'create_group': {
      const name = input.name || 'Untitled';
      const users = input.users;
      return `**Create group** "${name}" with ${users?.length || 0} creators`;
    }
    case 'add_users_to_group': {
      const uuids = input.user_uuids;
      return `**Add ${uuids?.length || 0} creators** to group #${input.group_id}`;
    }
    case 'remove_users_from_group': {
      const uuids = input.user_uuids;
      return `**Remove ${uuids?.length || 0} creators** from group #${input.group_id}`;
    }
    case 'delete_group':
      return `**Delete group** #${input.group_id}`;
    case 'reply_to_creator': {
      const msg = input.message || '';
      return `**Reply to creator:**\n\n> ${msg}`;
    }
    case 'send_to_creator': {
      const name = input.creator_name || '?';
      const msg = input.message || '';
      return `**Send message to ${name}:**\n\n> ${msg}`;
    }
    case 'update_org_memory': {
      const content = input.content || '';
      return `**Update org memory:**\n\n${content}`;
    }
    case 'send_connection_invite': {
      const name = input.creator_name || input._resolved_creator_name || 'creator';
      return `**Send connection invite** to ${name}`;
    }
    case 'send_connection_invites_bulk': {
      const creators = input.creators || [];
      const names = creators.map(creator => creator.creator_name).filter(Boolean);
      const list = names.length <= 10
        ? names.map(name => `- ${name}`).join('\n')
        : `${names.slice(0, 10).map(name => `- ${name}`).join('\n')}\n- _...and ${names.length - 10} more_`;
      return `**Send connection invites** to ${names.length} creator${names.length === 1 ? '' : 's'}:\n\n${list}`;
    }
    case 'execute_connection_invite_proposal': {
      const creators = input._resolved_creators || [];
      const names = creators.map(creator => creator.creator_name).filter(Boolean);
      if (names.length === 0) {
        return `**Send connection invite${names.length === 1 ? '' : 's'}**`;
      }
      if (names.length === 1) {
        return `**Send connection invite** to ${names[0]}`;
      }
      const list = names.length <= 10
        ? names.map(name => `- ${name}`).join('\n')
        : `${names.slice(0, 10).map(name => `- ${name}`).join('\n')}\n- _...and ${names.length - 10} more_`;
      return `**Send connection invites** to ${names.length} creators:\n\n${list}`;
    }
    case 'create_basic_discount_code': {
      const code = input.code;
      const purpose = input.purpose;
      const cg = input.customerGets;
      const pct = cg?.value?.percentage;
      const amt = cg?.value?.discountAmount?.amount;
      const valueStr = pct != null ? `${Math.round(pct * 100)}% off` : amt != null ? `$${amt} off` : 'discount';
      const assigned = input.assigned_user ? ` (assigned to creator UUID ${input.assigned_user.slice(0, 8)}...)` : ' (unassigned)';
      return `**Create discount code** \`${code}\` - ${valueStr}, purpose: **${purpose}**${assigned}`;
    }
    case 'create_free_shipping_discount_code': {
      const code = input.code;
      const purpose = input.purpose;
      const assigned = input.assigned_user ? ' (assigned)' : ' (unassigned)';
      return `**Create free-shipping discount code** \`${code}\` - purpose: **${purpose}**${assigned}`;
    }
    case 'create_gift_code_for_creator': {
      const code = input.code;
      const shipCode = input.paired_shipping_code;
      const amount = input.amount_usd;
      const creatorShort = input.creator_uuid?.slice(0, 8) || '?';
      return `**Create gift code for creator** ${creatorShort}...\n- Code: \`${code}\` ($${amount} off)\n- Free shipping pair: \`${shipCode}\``;
    }
    case 'assign_discount_code': {
      const codeId = input.code_id;
      const target = input.assigned_user ? `creator UUID ${input.assigned_user.slice(0, 8)}...` : 'unassign';
      const purpose = input.purpose ? ` (purpose: ${input.purpose})` : '';
      return `**Assign discount code** #${codeId} -> ${target}${purpose}`;
    }
    case 'activate_discount_code':
      return `**Activate discount code** #${input.code_id}`;
    case 'deactivate_discount_code':
      return `**Deactivate discount code** #${input.code_id}`;
    case 'bulk_activate_discount_codes': {
      const codes = input.codes || [];
      return `**Activate ${codes.length} discount codes**${codes.length <= 10 ? ` (#${codes.join(', #')})` : ''}`;
    }
    case 'bulk_deactivate_discount_codes': {
      const codes = input.codes || [];
      return `**Deactivate ${codes.length} discount codes**${codes.length <= 10 ? ` (#${codes.join(', #')})` : ''}`;
    }
    case 'retry_failed_messages':
      return `**Retry failed messages** for task ${input.task_id}`;
    default: {
      const lines = Object.entries(input).map(([k, v]) => {
        if (Array.isArray(v)) return `- ${k}: ${v.length} items`;
        if (typeof v === 'string' && v.length > 100) return `- ${k}: ${v.slice(0, 100)}...`;
        return `- ${k}: ${v}`;
      });
      return `**${getToolLabel(toolName)}**\n${lines.join('\n')}`;
    }
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FLOATING_INTRO = 'I can source creators, draft messages, suggest gifts and bonuses, or explain any number on the analytics screen. Anything I suggest still needs your approval.';

const AIAssistantMessages = ({ messages, sendMessage, addToolApprovalResponse, avatarUrl = null, firstName = '', status = null, authError = null, isOpen = false, hideSuggestions = false, evaluationStatus = null, bufferStatus = null, hasDraft = false, mode = null }) => {
  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const { suggestions: promptSuggestions, suggestionsToDisplay } = useSelector(state => state.aiPromptSuggestions);
  const { mode: globalAiAssistantMode } = useSelector(state => state.aiAssistant || { mode: 'sidebar' });
  // The chat panel embeds its own instance, so the global assistant's mode must not drive it.
  const aiAssistantMode = mode || globalAiAssistantMode;
  const isFloating = aiAssistantMode === 'floating';
  const { pathname, search } = useLocation();

  const suggestionsTypeByRoute = useMemo(() => {
    if (pathname.startsWith('/performance')) return 'performance';
    if (pathname.startsWith('/discover')) return 'discover';
    if (pathname.startsWith('/bonuses')) return 'campaigns';
    if (pathname.startsWith('/chat')) return 'messaging';
    if (pathname.startsWith('/my-creators')) {
      const tab = new URLSearchParams(search).get('tab');
      return tab === 'groups' ? 'groups' : 'creators';
    }
    return 'default';
  }, [pathname, search]);

  const openedViaAIAssistantSuggestions = Boolean(suggestionsToDisplay);

  const getSuggestionsToShow = () => {
    if (promptSuggestions?.[suggestionsTypeByRoute]?.length) return promptSuggestions[suggestionsTypeByRoute];
    if (promptSuggestions?.default?.length) return promptSuggestions.default;
    return [];
  };

  const suggestionsToShow = getSuggestionsToShow();
  const shouldShowSuggestions = suggestionsToShow.length > 0 && !authError && !hideSuggestions;
  const [isSuggestionsExpanded, setIsSuggestionsExpanded] = useState(messages.length === 0 || openedViaAIAssistantSuggestions);

  useEffect(() => {
    if (isOpen) {
      setIsSuggestionsExpanded(messages.length === 0 || openedViaAIAssistantSuggestions);
    }
  }, [isOpen, openedViaAIAssistantSuggestions]);

  useEffect(() => {
    if (messages.length === 0) {
      setIsSuggestionsExpanded(true);
    }
  }, [messages.length]);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  }, []);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setIsAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
  }, []);

  // Scroll to bottom instantly whenever the panel becomes visible
  useEffect(() => {
    if (isOpen) {
      scrollToBottom('instant');
    }
  }, [isOpen, scrollToBottom]);

  // Auto-scroll on new messages only if already near bottom
  useEffect(() => {
    if (isAtBottom) {
      scrollToBottom('smooth');
    }
  }, [messages]); // eslint-disable-line

  // const formatMessageTime = createdAt => {
  //   if (!createdAt) return '';
  //   return new Date(createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  // };

  const markdownComponents = useMemo(() => ({
    a: ({ href, children, ...props }) => {
      if (href && APP_URL) {
        const cleanAppUrl = APP_URL.endsWith('/') ? APP_URL.slice(0, -1) : APP_URL;
        if (href.startsWith(cleanAppUrl)) {
          const rawPath = href.slice(cleanAppUrl.length) || '/';
          const path = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
          return (
            <Link
              to={path}
              {...props}
              onClick={event => {
                // eslint-disable-next-line react/prop-types
                props.onClick?.(event);
                if (!event.defaultPrevented && (aiAssistantMode === 'fullscreen' || aiAssistantMode === 'floating')) {
                  dispatch(setAiAssistantMode('sidebar'));
                }
              }}
            >
              {children}
            </Link>
          );
        }
      }
      return <a href={href} rel="noopener noreferrer" target="_blank" {...props}>{children}</a>;
    }
  }), [aiAssistantMode, dispatch]);

  const getMessageText = message => {
    if (!message?.parts) return message?.content || '';
    return message.parts
      .filter(part => part.type === 'text')
      .map(part => part.text)
      .join('');
  };

  const handleCopyMessage = async message => {
    const text = getMessageText(message);
    if (!text || !navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // No-op if clipboard API is unavailable.
    }
  };

  const handleRetryMessage = message => {
    const msgIndex = messages.findIndex(m => m.id === message.id);
    if (msgIndex <= 0) return;
    for (let i = msgIndex - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        const text = getMessageText(messages[i]);
        if (text) sendMessage({ text });
        break;
      }
    }
  };

  const handleExportCsv = message => {
    const markdown = getMessageMarkdown(message);
    const table = extractFirstMarkdownTable(markdown);
    if (!table?.length) return;

    const csv = tableToCsv(table);
    const suffix = String(message?.id || 'assistant').replace(/[^a-z0-9_-]+/gi, '_');
    downloadCsv(csv, `ai-assistant-table-${suffix}.csv`);
  };

  const getRenderableMarkdownSections = message => {
    const markdown = getMessageMarkdown(message);
    const isStreamingAssistantMessage = isStreaming
      && message?.id === lastMessage?.id
      && message?.role === 'assistant';

    if (!isStreamingAssistantMessage || !hasMarkdownTableSyntax(markdown)) {
      return [{ type: 'markdown', content: markdown }];
    }

    return getMarkdownSectionsWithTablePlaceholders(markdown);
  };

  const renderAssistantMarkdown = (message, keyPrefix = 'assistant-markdown') => getRenderableMarkdownSections(message).map((section, index) => {
    if (section.type === 'table-placeholder') {
      return (
        <div key={`${keyPrefix}-table-${index}`} className="aiAssistantMessages-streamingMessage">
          <span className="aiAssistantMessages-streamingSpinner" aria-hidden="true" />
          <span>Generating table...</span>
        </div>
      );
    }

    return (
      <Markdown
        key={`${keyPrefix}-markdown-${index}`}
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
      >
        {section.content}
      </Markdown>
    );
  });

  const renderParts = message => {
    let hasRenderedAssistantText = false;

    if (!message?.parts?.length) {
      return message.role === 'user'
        ? (
          <div className="aiAssistantMessages-markdown">
            <p>{message.content}</p>
          </div>
        )
        : (
          <div className="aiAssistantMessages-markdown">
            {renderAssistantMarkdown(message)}
          </div>
        );
    }

    return message.parts.map((part, i) => {
      if (part.type === 'text') {
        if (message.role === 'user') {
          return (
            <div key={i} className="aiAssistantMessages-markdown">
              <p>{formatUserText(part.text)}</p>
            </div>
          );
        }

        if (hasRenderedAssistantText) {
          return null;
        }

        hasRenderedAssistantText = true;

        return (
          <div key={i} className="aiAssistantMessages-markdown">
            {renderAssistantMarkdown(message, `assistant-part-${i}`)}
          </div>
        );
      }

      if (isToolUIPart(part)) {
        const state = getToolPartState(part);

        if (state === 'waiting-approval') {
          const toolName = getToolName(part);
          const rawInput = Object.assign({}, getToolInput(part) || {});
          const approval = getToolApproval(part);

          if (!approval?.id) return null;

          if (toolName === 'send_connection_invite' && !rawInput.creator_name) {
            const uuid = rawInput.creator_uuid;
            if (uuid) {
              const resolved = resolveCreatorNameFromMessages(messages, uuid);
              if (resolved) rawInput._resolved_creator_name = resolved;
            }
          }

          if (toolName === 'execute_connection_invite_proposal') {
            const proposalId = rawInput.proposal_id;
            const pinnedCreators = resolvePinnedCreatorsFromMessages(messages, proposalId);
            if (pinnedCreators) {
              rawInput._resolved_creators = pinnedCreators;
            }
          }

          if (toolName === 'send_message_to_group') {
            const groupName = rawInput.group_name;
            const groupId = rawInput.group_id;
            const groupInfo = resolveGroupInfoFromMessages(messages, { groupId, groupName });

            if (groupName) {
              rawInput._resolved_group_name = groupName;
            } else if (groupInfo?.name) {
              rawInput._resolved_group_name = groupInfo.name;
            }

            if (groupInfo?.total_users != null) {
              rawInput._resolved_group_total = groupInfo.total_users;
            }
          }

          const summary = formatApprovalSummary(toolName, rawInput);
          const callId = getToolCallId(part);

          return (
            <div key={callId || i} className="aiAssistantMessages-approval">
              <div className="aiAssistantMessages-approvalBody aiAssistantMessages-markdown">
                <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{summary}</Markdown>
              </div>
              <div className="aiAssistantMessages-approvalActions">
                <button
                  className="aiAssistantMessages-approve"
                  type="button"
                  onClick={() => addToolApprovalResponse({ id: approval.id, approved: true })}
                >
                  Approve
                </button>
                <button
                  className="aiAssistantMessages-reject"
                  type="button"
                  onClick={() => addToolApprovalResponse({ id: approval.id, approved: false })}
                >
                  Reject
                </button>
              </div>
            </div>
          );
        }

        return null;
      }

      // Marker stamped by the server when this message was replaced by the evaluator's repair pass
      if (part.type === 'data-evaluation') {
        return (
          <div
            key={i}
            className="aiAssistantMessages-evalBadge"
            title="This response was automatically revised after a data verification check."
          >
            Revised after a data check
          </div>
        );
      }

      return null;
    });
  };

  const isStreaming = status === 'streaming';
  const activeTools = [];
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const streamingMessageText = lastMessage?.role === 'assistant' ? getMessageText(lastMessage).trim() : '';
  const shouldShowStreamingPlaceholder = isStreaming && !streamingMessageText;
  // Only the newest reply offers quick replies, and only once its turn has finished.
  const quickReplyChoices = lastMessage?.role === 'assistant' && status !== 'streaming' && status !== 'submitted'
    ? getQuickReplyChoices(lastMessage)
    : [];
  // The reply's own quick replies replace the generic suggestions
  const hasQuickReplies = quickReplyChoices.length > 0;

  if (isStreaming && messages.length > 0) {
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.role === 'assistant') {
      for (const part of lastMsg.parts || []) {
        const partState = getToolPartState(part);
        if (isToolUIPart(part) && (partState === 'loading' || partState === 'input-available')) {
          activeTools.push(getToolName(part));
        }
      }
    }
  }

  return (
    <>
      <div className="aiAssistantMessages-wrapper">
        <div className="aiAssistantMessages-body" ref={containerRef} onScroll={handleScroll}>
        {authError && (
          <div className="aiAssistantMessages-authError">{authError}</div>
        )}
        {messages.length === 0 && !authError && isFloating && (
          <div className="aiAssistantMessages-empty aiAssistantMessages-empty--floating">
            <SparklesFilledIcon className="aiAssistantMessages-emptyIcon--floating" />
            <p className="aiAssistantMessages-emptyIntro">{FLOATING_INTRO}</p>
          </div>
        )}
        {messages.length === 0 && !authError && !isFloating && (
          <div className="aiAssistantMessages-empty">
            <div className="aiAssistantMessages-emptyHeading">
              <SparklesOutlinedIcon className="aiAssistantMessages-emptyIcon" />
              {firstName ? (
                <h2 className="aiAssistantMessages-emptyTitle">Hi! {firstName}</h2>
              ) : (
                <h4 className="aiAssistantMessages-emptyTitle">Hi! I am your AI assistant what do you want to achieve today?</h4>
              )}
            </div>
            {firstName && (
              <p className="aiAssistantMessages-emptySubtitle">Hi! I am your AI assistant what do you want to achieve today?</p>
            )}
          </div>
        )}
        {messages.map(message => {
          const markdown = getMessageMarkdown(message);
          const hasTable = message.role === 'assistant' && Boolean(extractFirstMarkdownTable(markdown));
          const isEmptyStreamingAssistantMessage = isStreaming
            && message.id === lastMessage?.id
            && message.role === 'assistant'
            && !getMessageText(message).trim();

          if (isEmptyStreamingAssistantMessage) {
            return null;
          }

          if (message.role === 'user') {
            return (
              <div key={message.id} className={c('aiAssistantMessages-message', message.role)}>
                <div className="aiAssistantMessages-userRow">
                  <div className="aiAssistantMessages-userContent">
                    <div className="aiAssistantMessages-messageBubble">
                      {renderParts(message)}
                    </div>
                    <div className="aiAssistantMessages-userMeta">You{/* • {formatMessageTime(message.createdAt)}*/}</div>
                  </div>
                  <div className="aiAssistantMessages-userAvatar">
                    {avatarUrl && <img alt="User avatar" src={avatarUrl} />}
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div key={message.id} className={c('aiAssistantMessages-message', message.role)}>
              <div className="aiAssistantMessages-assistantRow">
                <div className="aiAssistantMessages-assistantAvatar" aria-hidden="true">
                  <SparklesOutlinedIcon />
                </div>
                <div className="aiAssistantMessages-assistantContent">
                  <div className="aiAssistantMessages-messageBubble">
                    {renderParts(message)}
                  </div>
                  {message.id === lastMessage?.id && quickReplyChoices.length > 0 && (
                    <AIAssistantQuickReplies
                      choices={quickReplyChoices}
                      isFloating={isFloating}
                      onSelect={choice => sendMessage({ text: choice })}
                    />
                  )}
                  {!isStreaming && (
                    <div className="aiAssistantMessages-assistantMetaRow">
                      <div className="aiAssistantMessages-assistantMeta">Split bot{/* • {formatMessageTime(message.createdAt)}*/}</div>
                      <div className="aiAssistantMessages-assistantActions">
                        {hasTable && (
                          <button
                            aria-label="Export table as CSV"
                            className="icon-button"
                            onClick={() => handleExportCsv(message)}
                            type="button"
                          >
                            <FileDownloadIcon />
                          </button>
                        )}
                        <button
                          aria-label="Copy message"
                          className="icon-button"
                          onClick={() => handleCopyMessage(message)}
                          type="button"
                        >
                          <CopyIcon />
                        </button>
                        <button
                          aria-label="Retry response"
                          className="icon-button"
                          onClick={() => handleRetryMessage(message)}
                          type="button"
                        >
                          <RefreshIcon />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {shouldShowStreamingPlaceholder && (
          <div className={c('aiAssistantMessages-message', 'assistant')}>
            <div className="aiAssistantMessages-assistantRow">
              <div className="aiAssistantMessages-assistantAvatar" aria-hidden="true">
                <SparklesOutlinedIcon />
              </div>
              <div className="aiAssistantMessages-assistantContent">
                <div className="aiAssistantMessages-messageBubble aiAssistantMessages-streamingMessage">
                  {activeTools.length > 0 ? activeTools.map(getToolLabel).join(', ') : (bufferStatus || 'Formulating response...')}
                </div>
              </div>
            </div>
          </div>
        )}
          <div ref={messagesEndRef} />
        </div>
        {!isAtBottom && (
          <button
            aria-label="Jump to latest messages"
            className="aiAssistantMessages-jumpToBottom"
            onClick={() => scrollToBottom('smooth')}
            type="button"
          >
            <KeyboardArrowDownIcon />
          </button>
        )}
      </div>

      {evaluationStatus && !isStreaming && (
        <div className="aiAssistantMessages-activityBar aiAssistantMessages-evalBar">
          <span className="aiAssistantMessages-spinner" />
          <span>
            {evaluationStatus === 'repairing'
              ? 'Found something to correct - revising this response...'
              : 'Double-checking this response against your data...'}
          </span>
        </div>
      )}

      {shouldShowSuggestions && isFloating && isSuggestionsExpanded && !hasDraft && !hasQuickReplies && (
        <div className="aiAssistantMessages-quickActions">
          {suggestionsToShow.map((suggestion, i) => (
            <button
              key={i}
              className="aiAssistantMessages-quickAction"
              type="button"
              onClick={() => {
                sendMessage({ text: suggestion });
                setIsSuggestionsExpanded(false);
              }}
            >
              {suggestion}
            </button>
          ))}
          <button
            aria-label="Hide suggestions"
            className="aiAssistantMessages-quickActionsDismiss"
            title="Hide suggestions"
            type="button"
            onClick={() => setIsSuggestionsExpanded(false)}
          >
            <CloseDarkIcon />
          </button>
        </div>
      )}

      {shouldShowSuggestions && !isFloating && !hasQuickReplies && (
        <div className="aiAssistantMessages-suggestions">
          <div className="aiAssistantMessages-suggestionsHeader">
            <span className="aiAssistantMessages-suggestionsLabel">Suggestions:</span>
            <div className="aiAssistantMessages-suggestionsDivider" />
            <button
              className="aiAssistantMessages-suggestionsToggle"
              type="button"
              onClick={() => setIsSuggestionsExpanded(v => !v)}
            >
              <span className="aiAssistantMessages-suggestionsState">{isSuggestionsExpanded ? 'Hide' : 'Show'}</span>
            </button>
          </div>
          {isSuggestionsExpanded && (
            <div className="aiAssistantMessages-suggestionsChips">
              {suggestionsToShow.map((suggestion, i) => (
                <button
                  key={i}
                  className="aiAssistantMessages-suggestionChip"
                  type="button"
                  onClick={() => {
                    sendMessage({ text: suggestion });
                    setIsSuggestionsExpanded(false);
                  }}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

AIAssistantMessages.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.object).isRequired,
  sendMessage: PropTypes.func.isRequired,
  addToolApprovalResponse: PropTypes.func.isRequired,
  avatarUrl: PropTypes.string,
  firstName: PropTypes.string,
  status: PropTypes.string,
  authError: PropTypes.string,
  isOpen: PropTypes.bool,
  hideSuggestions: PropTypes.bool,
  evaluationStatus: PropTypes.oneOf(['checking', 'repairing']),
  bufferStatus: PropTypes.string,
  hasDraft: PropTypes.bool,
  mode: PropTypes.oneOf(['sidebar', 'fullscreen', 'floating', 'panel'])
};

export default AIAssistantMessages;
